import React, { useCallback, memo } from 'react';
import { Paragraph } from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';

export type StudyLegendProps = {
  className?: string;
  type?: string;
  result?: string;
  showLabel?: boolean;
};

export const StudyLegendItem: React.FC<{
  highlightColor?: string;
  label?: string;
  title: string;
}> = ({ highlightColor, label, title }) => (
  <p className="flex items-center gap-x-4">
    {label && (
      <Paragraph size="small" variant="medium" tagName="span">
        {label}
      </Paragraph>
    )}
    <Paragraph
      className={['flex items-center gap-x-2', label ? 'font-medium' : ''].join(
        ' ',
      )}
      size="small"
      tagName="span"
    >
      {highlightColor && (
        <span
          aria-hidden="true"
          className={['inline-grid rounded-full w-2 h-2', highlightColor].join(
            ' ',
          )}
        ></span>
      )}
      {title}
    </Paragraph>
  </p>
);

export const StudyLegend: React.FC<StudyLegendProps> = ({
  className,
  type,
  result,
  showLabel = true,
}) => {
  const { translations } = useWordPressContext();

  const getLegendColor = useCallback(() => {
    switch (result) {
      case 'Positive':
        return 'bg-green-400';
      case 'Negative':
        return 'bg-red-600';
      default:
        return 'bg-gray-300';
    }
  }, [result]);

  return (
    <footer
      className={[
        'grid content-center items-center',
        type && result
          ? 'justify-start sm:grid-flow-col md:grid-cols-2 gap-x-6'
          : '',
        className,
      ].join(' ')}
    >
      {type && (
        <StudyLegendItem
          label={showLabel ? translations['Type of Study'] : null}
          title={translations[type] || type}
        />
      )}
      {result && (
        <StudyLegendItem
          highlightColor={getLegendColor()}
          label={showLabel ? translations['Result'] : null}
          title={translations[result] || result}
        />
      )}
    </footer>
  );
};

export default memo(StudyLegend);
